/*
 * Copyright (c) 2026 CalAmp Corp.  All Rights Reserved
 */
import React, {useCallback, useMemo} from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {EventCodeValueType, getEventName} from '@ion/core/src/services/EventCodes';
import {GenericObject} from '@ion/core/src/types/entities';
import {dlsColor} from '../../../theme/theme';
import {getFormattedDateTimeZoneParts} from '../../../utils/ComponentUtils';
import IonTooltip from '../../tooltip/IonTooltip';
import IonVisionThumbnail from '../IonVisionThumbnail';

export interface VisionHistoryCardProps {
  event: GenericObject;
  eventClick?: (data: GenericObject) => void;
  hasVideo?: boolean;
  toolTipLabel?: string;
  showDivider?: boolean;
}

type StyleProps = Pick<VisionHistoryCardProps, 'hasVideo' | 'showDivider'>;

const useStyles = makeStyles(() => ({
  row : {
    boxSizing       : 'border-box',
    display         : 'flex',
    alignItems      : 'flex-start',
    height          : '92px',
    padding         : '16px',
    gap             : '24px',
    backgroundColor : dlsColor('white'),
    borderBottom    : ({showDivider}: StyleProps) => showDivider ? `1px solid ${dlsColor('grey7')}` : 'none'
  },
  thumbnail : {
    width           : '120px',
    height          : '60px',
    flexShrink      : 0,
    backgroundColor : dlsColor('grey5'),
    '& img'         : {
      width     : '100%',
      height    : '100%',
      objectFit : 'cover'
    }
  },
  info : {
    display        : 'flex',
    flexDirection  : 'column',
    justifyContent : 'center',
    gap            : '8px'
  },
  title : {
    fontFamily    : 'Roboto',
    fontWeight    : 500,
    fontSize      : '13px',
    lineHeight    : '16px',
    letterSpacing : '0.5px',
    color         : ({hasVideo}: StyleProps) => hasVideo ? '#276AD8' : dlsColor('grey3'),
    cursor        : ({hasVideo}: StyleProps) => hasVideo ? 'pointer' : 'default'
  },
  timestampGroup : {
    display       : 'flex',
    flexDirection : 'column'
  },
  timestamp : {
    fontFamily    : 'Inter',
    fontWeight    : 600,
    fontSize      : '12px',
    lineHeight    : '16px',
    letterSpacing : '0px',
    verticalAlign : 'middle',
    color         : dlsColor('grey2')
  }
}));

/**
 * VisionHistoryCard renders a single row of the current theme's Vision
 * history list - a thumbnail, an event title link, and its timestamp -
 * composing the existing `IonVisionThumbnail` for the image and reusing the
 * same event-name/date formatting `VisionEventItem` already relies on, so a
 * later integration pass can wire the exact same click and
 * video-availability behavior, only restyled rather than rebuilt.
 *
 * ```typescript
 *
 *   import {VisionHistoryCard} from
 *     '@ion/dls/src/components/vision/current/VisionHistoryCard';
 *   ...
 *
 *   <VisionHistoryCard event={event}
 *     eventClick={function(data) { onIonVisionClick(data); }}
 *     toolTipLabel='This Video Is No Longer Available'
 *     showDivider
 *     hasVideo />
 *
 * ```
 *
 */
export const VisionHistoryCard = ({
  event,
  eventClick,
  hasVideo = true,
  toolTipLabel,
  showDivider = true
}: VisionHistoryCardProps) => {
  const classes = useStyles({hasVideo, showDivider});

  const {date, time} = useMemo(() => (
    getFormattedDateTimeZoneParts(event?.appmsgEventTime)
  ), [event?.appmsgEventTime]);

  const onTitleClick = useCallback(function() {
    if (hasVideo) {
      eventClick?.(event);
    }
  }, [hasVideo, eventClick, event]);

  const titleElement = (
    <span className={classes.title} onClick={onTitleClick}>
      {getEventName({
        from : EventCodeValueType.eventCode,
        val  : parseInt(event?.avlEventCode)
      })}
    </span>
  );

  return (
    <div className={classes.row}>
      <div className={classes.thumbnail}>
        <IonVisionThumbnail data={event} onClick={hasVideo ? eventClick : undefined} />
      </div>
      <div className={classes.info}>
        {!hasVideo && toolTipLabel ? (
          <IonTooltip caption={toolTipLabel}>
            {titleElement}
          </IonTooltip>
        ) : titleElement}
        <div className={classes.timestampGroup}>
          <span className={classes.timestamp}>
            {date}
          </span>
          <span className={classes.timestamp}>
            {time}
          </span>
        </div>
      </div>
    </div>
  );
};

export default React.memo(VisionHistoryCard);
